import {
  type KeyboardEvent,
  type ChangeEventHandler,
  useState,
  forwardRef,
  useImperativeHandle,
  useRef,
  FocusEvent,
} from 'react';

import type { ITagsInput } from '../../types/Input/input';
import type { ITagProps } from '../../types/tag/tag';

import { Tag } from '../Tag';

import { generateUUID } from '../../utils/generateUUID';

import styles from './input.module.scss';

export const TagsInput = forwardRef<HTMLInputElement, ITagsInput>((props, ref) => {
  const {
    tags = [],
    onTagAdd,
    onTagRemove,
    tagValidator,
    maxTags,
    allowDuplicates = false,
    tagDelimiters = [',', 'Enter', ' '],
    placeholder,
    disabled,
    isMultiple,
    ...inputProps
  } = props;

  const [inputValue, setInputValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  const isTagValid = (tag: ITagProps) => {
    const trimmedTag = tag.text.trim();

    const hasValue = !!trimmedTag;
    const isWithinTagsLimit = !maxTags || tags.length < maxTags;
    const isUnique = allowDuplicates || !tags.some(existingTag => existingTag.id === tag.id || existingTag.text.toLowerCase() === trimmedTag.toLowerCase());
    const isCustomValidationPassed = !tagValidator || tagValidator({ ...tag, text: trimmedTag });

    return hasValue && isWithinTagsLimit && isUnique && isCustomValidationPassed;
  };

  const getPlaceholder = () => {
    if (isFocused && isMultiple === false && tags.length) {
      return tags[0].text;
    }

    if (!tags.length) return placeholder ?? '';
    return '';
  };

  const addTag = (tag: ITagProps) => {
    const trimmedTag = tag.text.trim();
    onTagAdd?.({ ...tag, text: trimmedTag });

    if (isMultiple === false) {
      inputRef.current?.blur();
    }
  };

  const handleInputChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const value = e.target.value;
    const lastChar = value[value.length - 1];

    if (inputProps.onChange) {
      inputProps.onChange(e);
    }

    if (tagDelimiters.includes(lastChar)) {
      const newTag = { text: value.slice(0, -1), id: generateUUID(), type: 'text' } as ITagProps;

      if (isTagValid(newTag)) {
        addTag(newTag);
        setInputValue('');
      }
    } else {
      setInputValue(value);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    inputProps.onKeyDown?.(e);
    if (e.defaultPrevented) return;

    if (e.key === 'Enter') {
      e.preventDefault();
      const newTag = { text: inputValue, id: generateUUID(), type: 'text' } as ITagProps;

      if (isTagValid(newTag)) {
        addTag(newTag);
        setInputValue('');
      }
    } else if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
      const lastTag = tags[tags.length - 1];
      onTagRemove?.(lastTag);
    }
  };

  const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
    inputProps.onFocus?.(e);
    setIsFocused(true);
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    inputProps.onBlur?.(e);
    setIsFocused(false);
  }

  const renderTags = () => {    
    if (isMultiple === false) {
      return (
        <div className={styles['container__wrapper__tags__single']}>
          {!isFocused && !!tags.length ? (
            <Tag type="text" size="sm" {...tags[0]} />
          ) : (
            ''
          )}
        </div>
      );
    }

    return tags.map((tag) => (
      <Tag
        type='text'
        size="sm"
        {...tag}
        key={tag.id}
        onClose={() => onTagRemove?.(tag)}
      />
    ))
  };

  useImperativeHandle(ref, () => {
    return Object.assign(inputRef.current!, {
      handleSetInputValue: (value: string) => {
        setInputValue(value ?? '');
      },
    });
  });

  return (
    <div className={styles['container__wrapper__tags']}>
      {renderTags()}
      <input
        {...inputProps}
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={getPlaceholder() as string}
        disabled={disabled}
        className={styles['container__wrapper__tags__input']}
        onFocus={handleFocus}
        onBlur={handleBlur}
      />
    </div>
  );
}); 